import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Language, SearchFilters } from '@/types';
import { Button, Card } from '@/components/common';
import { getTranslation, SERVICE_CATEGORIES, PAYMENT_PREFERENCES, SERVICE_TYPES } from '@/constants';

interface SearchFilterProps {
  language: Language;
  onSearch: (filters: SearchFilters) => void;
}

// Debounce hook
const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const SearchFilter: React.FC<SearchFilterProps> = ({ language, onSearch }) => {
  const t = getTranslation(language);

  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('');
  const [paymentPreference, setPaymentPreference] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Debounce search query for auto-search
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Get suggestions based on input
  const getSuggestions = useCallback(() => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase();
    const suggestions: { label: string; type: 'service' | 'category'; id: string }[] = [];

    // Search in service types
    SERVICE_TYPES.forEach((service) => {
      const name = service.name[language].toLowerCase();
      if (name.includes(query)) {
        suggestions.push({
          label: `${service.icon} ${service.name[language]}`,
          type: 'service',
          id: service.id,
        });
      }
    });

    // Search in categories
    Object.entries(SERVICE_CATEGORIES).forEach(([key, names]) => {
      const name = names[language].toLowerCase();
      if (name.includes(query)) {
        suggestions.push({
          label: names[language],
          type: 'category',
          id: key,
        });
      }
    });

    return suggestions.slice(0, 6); // Limit to 6 suggestions
  }, [searchQuery, language]);

  const suggestions = getSuggestions();

  // Check if any filters are active
  const hasActiveFilters = searchQuery.trim() !== '' || category !== '' || paymentPreference !== '';

  // Auto-search when debounced query or filters change
  useEffect(() => {
    onSearch({
      searchQuery: debouncedSearchQuery.trim() || undefined,
      category: category || undefined,
      paymentPreference: paymentPreference || undefined,
    });
  }, [debouncedSearchQuery, category, paymentPreference, onSearch]);

  const handleClear = () => {
    setSearchQuery('');
    setCategory('');
    setPaymentPreference('');
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: { label: string; type: 'service' | 'category'; id: string }) => {
    if (suggestion.type === 'category') {
      setCategory(suggestion.id);
      setSearchQuery('');
    } else {
      // Extract text without emoji for search
      const labelText = suggestion.label.replace(/^[^\s]+\s/, '');
      setSearchQuery(labelText);
    }
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          handleSuggestionClick(suggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Card className="mb-6">
      <div className="space-y-4">
        {/* Search Input with Autocomplete */}
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            placeholder={t.searchPlaceholder}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSuggestions(true);
              setSelectedSuggestionIndex(-1);
            }}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={handleKeyDown}
            className="w-full px-3 py-2.5 text-base text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoComplete="off"
          />

          {/* Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
            >
              {suggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion.type}-${suggestion.id}`}
                  type="button"
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition-colors flex items-center gap-2
                    ${index === selectedSuggestionIndex ? 'bg-blue-50' : ''}
                    ${index === 0 ? 'rounded-t-lg' : ''}
                    ${index === suggestions.length - 1 ? 'rounded-b-lg' : ''}
                  `}
                >
                  <span className="flex-1">{suggestion.label}</span>
                  <span className="text-xs text-gray-400">
                    {suggestion.type === 'category'
                      ? (language === 'en' ? 'Category' : 'Catégorie')
                      : (language === 'en' ? 'Service' : 'Service')
                    }
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.filterByCategory}
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t.allCategories}</option>
              {Object.entries(SERVICE_CATEGORIES).map(([key, names]) => (
                <option key={key} value={key}>
                  {names[language]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.filterByPayment}
            </label>
            <select
              value={paymentPreference}
              onChange={(e) => setPaymentPreference(e.target.value)}
              className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t.allPayments}</option>
              {PAYMENT_PREFERENCES.map((pref) => {
                const label =
                  pref.key === 'cash'
                    ? t.cad
                    : pref.key === 'points'
                    ? t.communityPoints
                    : t.eitherOption;

                return (
                  <option key={pref.key} value={pref.key}>
                    {pref.icon} {label}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        <Button
          onClick={hasActiveFilters ? handleClear : undefined}
          variant={hasActiveFilters ? 'secondary' : 'primary'}
          fullWidth
          disabled={!hasActiveFilters}
        >
          {hasActiveFilters
            ? t.clearFilters
            : (language === 'en' ? 'Find Services' : 'Trouver des services')
          }
        </Button>
      </div>
    </Card>
  );
};
